import type {
  CareDefinitions,
  CareRxEvent,
  CareSeries,
  CareSeriesPoint,
  CareTimeline,
  CareTrendPoint
} from '@/types/care'

/** The key the whole-questionnaire score is filed under, kept out of the calculator hash space. */
export const TOTAL_SERIES_KEY = '__total__'

const byDate = (a: CareTrendPoint, b: CareTrendPoint) =>
  new Date(a.submittedAt).getTime() - new Date(b.submittedAt).getTime()

/**
 * Where the vertical dividers come from.
 *
 * Both signals are already in the trend read, so this needs no second source:
 *
 * - **Rx change (dotted)** — `subscriptionHash` identifies the run a care check-in belongs to, and
 *   the model exists to "separate two runs of one questionnaire after a medication change". So a
 *   change of run between consecutive check-ins *is* the medication change, dated at the first
 *   check-in on the new run.
 * - **Rx renewal (solid)** — a `review` submission hangs off an order rather than a schedule, which
 *   is the measurement taken at the point the prescription is reviewed.
 *
 * Both are inferences from what the rail records, not events the estate emits by that name. If
 * prescribing later publishes real Rx events, this is the one function to replace.
 */
const toRxEvents = (points: CareTrendPoint[]): CareRxEvent[] => {
  const events: CareRxEvent[] = []
  let previousRun: string | null | undefined

  for (const point of points) {
    const date = new Date(point.submittedAt)

    if (point.type === 'review') {
      events.push({ date, kind: 'renewal', label: 'Rx renewed' })
      continue
    }

    // The first check-in establishes the run rather than changing it.
    if (previousRun !== undefined && point.subscriptionHash !== previousRun) {
      events.push({ date, kind: 'change', label: 'Rx change' })
    }
    previousRun = point.subscriptionHash
  }

  return events
}

const toSeriesPoint = (point: CareTrendPoint, value: number | null, max: number | null): CareSeriesPoint => ({
  date: new Date(point.submittedAt),
  value,
  // Guard the degenerate max: a calculator scored out of zero has no meaningful percentage.
  ratio: value !== null && max !== null && max > 0 ? value / max : null,
  submissionId: point.id
})

/**
 * Folds a page of check-ins into the series the timeline draws.
 *
 * `definitions` decides what exists: a calculator with no definition has a name only the authoring
 * tool knows, so it is dropped rather than drawn as a hash. That also makes the legend stable when
 * a module gains a section mid-treatment — the definition lists it, and its early points are simply
 * absent.
 *
 * Scope the caller's points to one questionnaire before calling: two modules answered on the same
 * day would otherwise contribute two values to one date on a single axis.
 */
export const toCareTimeline = (points: CareTrendPoint[], definitions: CareDefinitions): CareTimeline => {
  const ordered = [...points].sort(byDate)

  const series: CareSeries[] = definitions.calculators.map(calculator => ({
    key: calculator.hash,
    name: calculator.name,
    color: calculator.color,
    max: calculator.max,
    points: ordered.map(point => toSeriesPoint(point, point.scores.values?.[calculator.hash] ?? null, calculator.max))
  }))

  const total: CareSeries | null = definitions.total
    ? {
        key: TOTAL_SERIES_KEY,
        name: definitions.total.name,
        color: definitions.total.color,
        max: definitions.total.max,
        points: ordered.map(point => toSeriesPoint(point, point.scores.total, definitions.total?.max ?? null))
      }
    : null

  // Whether anything was actually scored, rather than whether check-ins exist. Until scoring lands
  // (CD-94) every value is null, and the chart needs to say that instead of drawing a flat zero.
  const hasScores = [...series, ...(total ? [total] : [])].some(entry =>
    entry.points.some(point => point.value !== null)
  )

  return {
    series,
    total,
    rxEvents: toRxEvents(ordered),
    dates: ordered.map(point => new Date(point.submittedAt)),
    hasScores
  }
}
